import React, { useEffect, useMemo, useState } from "react";
import { adminApi, formatErr } from "./api";
import { useEscapeKey } from "./hooks";
import { toast } from "sonner";
import { Plus, Trash2, X, CalendarClock, AlertTriangle } from "lucide-react";

const empty = {
  sku: "", batch_number: "", manufacturing_date: "", expiry_date: "",
  quantity: 0, warehouse_id: "", rack_number: "",
};

const alertStyle = (a) => {
  if (a === "critical") return { color: "text-rose-300", label: "Critical" };
  if (a === "warning") return { color: "text-amber-400", label: "Expiring soon" };
  return { color: "text-emerald-400", label: "Healthy" };
};

export default function AdminBatches() {
  const [batches, setBatches] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  useEscapeKey(open, () => setOpen(false));
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [b, p, w] = await Promise.all([
      adminApi.get("/batches"),
      adminApi.get("/products"),
      adminApi.get("/warehouses"),
    ]);
    setBatches(b.data.batches);
    setProducts(p.data.products);
    setWarehouses(w.data.warehouses);
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, quantity: Number(form.quantity) };
      if (!payload.warehouse_id) delete payload.warehouse_id;
      if (!payload.rack_number) delete payload.rack_number;
      await adminApi.post("/batches", payload);
      toast.success("Batch recorded");
      setOpen(false);
      setForm(empty);
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id, num) => {
    if (!confirm(`Delete batch ${num}?`)) return;
    try {
      await adminApi.delete(`/batches/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  const wmap = useMemo(() => Object.fromEntries(warehouses.map((w) => [w.id, w.name])), [warehouses]);
  const pmap = useMemo(() => Object.fromEntries(products.map((p) => [p.sku, p.name])), [products]);

  const filtered = filter === "all" ? batches : batches.filter((b) => b.expiry_alert === filter);

  const counts = {
    critical: batches.filter((b) => b.expiry_alert === "critical").length,
    warning: batches.filter((b) => b.expiry_alert === "warning").length,
    ok: batches.filter((b) => b.expiry_alert === "ok").length,
  };

  return (
    <div className="space-y-6" data-testid="admin-batches-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="label-eyebrow text-gold">Manufacturing</p>
          <h1 className="font-serif text-4xl text-bone mt-1">Batches & Expiry</h1>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary !py-3" data-testid="new-batch-btn">
          <Plus className="w-3.5 h-3.5" /> New batch
        </button>
      </div>

      {/* Alert tiles */}
      <div className="grid sm:grid-cols-3 gap-4">
        <button onClick={() => setFilter("critical")} className={`text-left bg-ink-900 border p-5 transition-colors ${filter === "critical" ? "border-burgundy" : "border-white/5 hover:border-burgundy/40"}`} data-testid="filter-critical">
          <div className="flex items-center gap-2 text-rose-300"><AlertTriangle className="w-4 h-4" /><span className="label-eyebrow">Critical · ≤90 days</span></div>
          <p className="num-display text-3xl text-bone mt-3">{counts.critical}</p>
        </button>
        <button onClick={() => setFilter("warning")} className={`text-left bg-ink-900 border p-5 transition-colors ${filter === "warning" ? "border-amber-400" : "border-white/5 hover:border-amber-400/40"}`} data-testid="filter-warning">
          <div className="flex items-center gap-2 text-amber-400"><CalendarClock className="w-4 h-4" /><span className="label-eyebrow">Warning · ≤180 days</span></div>
          <p className="num-display text-3xl text-bone mt-3">{counts.warning}</p>
        </button>
        <button onClick={() => setFilter("all")} className={`text-left bg-ink-900 border p-5 transition-colors ${filter === "all" ? "border-gold" : "border-white/5 hover:border-gold/40"}`} data-testid="filter-all">
          <div className="flex items-center gap-2 text-gold"><span className="label-eyebrow">All batches</span></div>
          <p className="num-display text-3xl text-bone mt-3">{batches.length}</p>
        </button>
      </div>

      <div className="bg-ink-900 border border-white/5 overflow-x-auto" data-testid="batches-table">
        <table className="w-full text-sm">
          <thead className="bg-ink-950">
            <tr className="text-left label-eyebrow text-mist">
              <th className="px-4 py-3">Batch #</th>
              <th className="px-4 py-3">SKU · Product</th>
              <th className="px-4 py-3">Mfg</th>
              <th className="px-4 py-3">Expiry</th>
              <th className="px-4 py-3">Days left</th>
              <th className="px-4 py-3">Qty</th>
              <th className="px-4 py-3">Warehouse</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => {
              const s = alertStyle(b.expiry_alert);
              return (
                <tr key={b.id} className="border-t border-white/5 text-bone/90 font-light" data-testid={`batch-row-${b.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{b.batch_number}</td>
                  <td className="px-4 py-3"><span className="font-mono text-xs text-mist">{b.sku}</span> · {pmap[b.sku] || "—"}</td>
                  <td className="px-4 py-3 text-mist text-xs">{b.manufacturing_date}</td>
                  <td className="px-4 py-3 text-mist text-xs">{b.expiry_date}</td>
                  <td className={`px-4 py-3 ${s.color}`}>{b.days_to_expiry ?? "—"}</td>
                  <td className="px-4 py-3">{b.quantity}</td>
                  <td className="px-4 py-3 text-mist text-xs">{b.warehouse_id ? wmap[b.warehouse_id] || "—" : "—"}{b.rack_number ? ` · ${b.rack_number}` : ""}</td>
                  <td className={`px-4 py-3 ${s.color}`}><span className="label-eyebrow">{s.label}</span></td>
                  <td className="px-4 py-3">
                    <button onClick={() => remove(b.id, b.batch_number)} className="text-mist hover:text-rose-300 p-1.5" data-testid={`delete-batch-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-mist font-light italic">No batches in this view.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-ink-900 border border-white/10 w-full max-w-xl p-8 space-y-4" data-testid="batch-form-dialog">
            <div className="flex justify-between items-center">
              <h3 className="font-serif text-2xl text-bone">New batch</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select required value={form.sku} onChange={set("sku")} className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="batch-sku-select">
                <option value="">— Select SKU —</option>
                {products.map((p) => <option key={p.sku} value={p.sku}>{p.sku} · {p.name}</option>)}
              </select>
              <input required value={form.batch_number} onChange={set("batch_number")} placeholder="Batch number" className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="batch-number-input" />
              <label className="text-xs text-mist">Mfg date
                <input required type="date" value={form.manufacturing_date} onChange={set("manufacturing_date")} className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              </label>
              <label className="text-xs text-mist">Expiry date
                <input required type="date" value={form.expiry_date} onChange={set("expiry_date")} className="mt-1 w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              </label>
              <input required type="number" min="1" value={form.quantity} onChange={set("quantity")} placeholder="Quantity" className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
              <select value={form.warehouse_id} onChange={set("warehouse_id")} className="bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold">
                <option value="">— Warehouse —</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
              <input value={form.rack_number} onChange={set("rack_number")} placeholder="Rack number" className="col-span-2 bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
            </div>
            <div className="flex justify-end gap-3 pt-3">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost !py-3">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary !py-3" data-testid="save-batch-btn">{busy ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
